/**
 * Membership-scoped shipping address SSOT (TenantCustomerMembership.shippingAddressJson).
 * Contract: GET/PUT `/customer-account/shipping-address` → `{ address: … | null }`.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '../../shared/api/ApiClient.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import {
  EMPTY_SHIPPING_ADDRESS,
  isShippingAddressComplete,
  type ShippingAddressDraft,
} from './shippingAddressTypes.js';

const SHIPPING_ADDRESS_PATH = '/customer-account/shipping-address';

export interface MembershipShippingAddressFields {
  recipientName?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  phone?: string | null;
  email?: string | null;
}

/** Envelope matching Get/PutMembershipShippingAddressUseCase. */
export interface MembershipShippingAddressDTO {
  address: MembershipShippingAddressFields | null;
}

function isMissingEndpointError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 405 || err.status === 501);
}

/** Prefill draft for settings editor — always returns a draft (empty when unset). */
export function membershipDtoToEditableDraft(
  dto: MembershipShippingAddressDTO | null | undefined,
): ShippingAddressDraft {
  const raw = dto?.address;
  if (raw == null || typeof raw !== 'object') {
    return { ...EMPTY_SHIPPING_ADDRESS };
  }
  return {
    recipientName: typeof raw.recipientName === 'string' ? raw.recipientName : '',
    line1: typeof raw.line1 === 'string' ? raw.line1 : '',
    line2: typeof raw.line2 === 'string' ? raw.line2 : '',
    city: typeof raw.city === 'string' ? raw.city : '',
    postalCode: typeof raw.postalCode === 'string' ? raw.postalCode : '',
    countryCode:
      typeof raw.countryCode === 'string' && raw.countryCode.trim().length === 2
        ? raw.countryCode.trim().toUpperCase()
        : 'CZ',
    phone: typeof raw.phone === 'string' ? raw.phone : '',
    email: typeof raw.email === 'string' ? raw.email : '',
  };
}

export function membershipAddressToDraft(
  dto: MembershipShippingAddressDTO | null | undefined,
): ShippingAddressDraft | null {
  const draft = membershipDtoToEditableDraft(dto);
  return isShippingAddressComplete(draft) ? draft : null;
}

export function draftToMembershipShippingAddressBody(
  draft: ShippingAddressDraft,
): { address: MembershipShippingAddressFields } {
  return {
    address: {
      recipientName: draft.recipientName.trim(),
      line1: draft.line1.trim(),
      line2: draft.line2.trim().length > 0 ? draft.line2.trim() : null,
      city: draft.city.trim(),
      postalCode: draft.postalCode.trim(),
      countryCode: draft.countryCode.trim().toUpperCase(),
      phone: draft.phone.trim().length > 0 ? draft.phone.trim() : null,
      email: draft.email.trim().length > 0 ? draft.email.trim() : null,
    },
  };
}

export function useMembershipShippingAddress(): UseQueryResult<
  MembershipShippingAddressDTO | null,
  Error
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.membershipShippingAddress(tenantCode),
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        return await api.request<MembershipShippingAddressDTO>(SHIPPING_ADDRESS_PATH);
      } catch (err) {
        if (isMissingEndpointError(err)) {
          return null;
        }
        throw err;
      }
    },
  });
}

export function useSaveMembershipShippingAddress(): UseMutationResult<
  MembershipShippingAddressDTO | null,
  Error,
  ShippingAddressDraft
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft) => {
      const body = draftToMembershipShippingAddressBody(draft);
      try {
        return await api.request<MembershipShippingAddressDTO>(SHIPPING_ADDRESS_PATH, {
          method: 'PUT',
          body,
        });
      } catch (err) {
        // Fallback: membership update shape if dedicated route missing.
        if (isMissingEndpointError(err)) {
          try {
            return await api.request<MembershipShippingAddressDTO>(
              '/customer-account/memberships/current',
              { method: 'PATCH', body },
            );
          } catch (fallbackErr) {
            if (isMissingEndpointError(fallbackErr)) {
              return null;
            }
            throw fallbackErr;
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      if (data != null) {
        queryClient.setQueryData(
          customerQueryKeys.membershipShippingAddress(tenantCode),
          data,
        );
      }
    },
  });
}
